import jsYaml from 'js-yaml';
import { SpecDocumentNode, SpecDocumentMeta, TaskItem, TaskStatus } from '../types/spec';
import { RawFileEntry } from './FileSystemIngestion';

export class ASTParserService {
  /**
   * Processes a list of raw markdown files into parsed SpecDocumentNodes
   */
  public static parseFileEntries(entries: RawFileEntry[]): SpecDocumentNode[] {
    return entries.map(entry => this.parseSingleEntry(entry));
  }

  public static parseSingleEntry(entry: RawFileEntry): SpecDocumentNode {
    const { meta, cleanContent } = this.extractFrontmatter(entry.content);
    const headings = this.extractHeadings(cleanContent);
    const tasks = this.extract5StateTasks(entry.path, cleanContent);

    // Extract numeric sequence order from filename e.g. 01-vision -> 1, 02-prd -> 2
    const numericMatch = entry.name.match(/^(\d+)-/);
    const numericOrder = numericMatch ? parseInt(numericMatch[1], 10) : (entry.name.toUpperCase().includes('TASK') ? 99 : 50);

    const wordCount = cleanContent.split(/\s+/).filter(Boolean).length;
    const readingTimeMinutes = Math.max(1, Math.ceil(wordCount / 200));

    const completed = tasks.filter(t => t.status === 'completed').length;
    const inProgress = tasks.filter(t => t.status === 'in-progress').length;
    const blocked = tasks.filter(t => t.status === 'blocked').length;
    const open = tasks.filter(t => t.status === 'open').length;
    const cancelled = tasks.filter(t => t.status === 'cancelled').length;

    const fullMeta: SpecDocumentMeta = {
      title: headings[0]?.text || meta.title || entry.name,
      docId: meta.doc_id || meta.docId || entry.name,
      status: meta.status || 'approved',
      author: meta.author || 'Specification Author',
      version: meta.version || '1.0.0',
      date: meta.date,
      readingTimeMinutes,
      taskStats: {
        total: tasks.length,
        completed,
        inProgress,
        blocked,
        open,
        cancelled
      }
    };

    return {
      path: entry.path,
      name: entry.name,
      folderPath: entry.folderPath || 'root',
      numericOrder,
      content: cleanContent,
      meta: fullMeta,
      headings,
      tasks
    };
  }

  /**
   * Extracts YAML frontmatter blocks bounded by ---
   */
  private static extractFrontmatter(content: string): { meta: Record<string, any>; cleanContent: string } {
    const match = content.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n/);
    if (!match) {
      return { meta: {}, cleanContent: content };
    }

    try {
      const yamlText = match[1];
      const parsed = (jsYaml.load(yamlText) as Record<string, any>) || {};
      const cleanContent = content.slice(match[0].length);
      return { meta: parsed, cleanContent };
    } catch {
      return { meta: {}, cleanContent: content };
    }
  }

  /**
   * Extracts H1, H2, H3 headings with slugified anchor IDs
   */
  private static extractHeadings(content: string): { depth: number; text: string; id: string }[] {
    const headings: { depth: number; text: string; id: string }[] = [];
    const lines = content.split('\n');

    lines.forEach(line => {
      const match = line.match(/^(#{1,3})\s+(.+)$/);
      if (match) {
        const depth = match[1].length;
        const text = match[2].trim().replace(/\*+/g, '');
        const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
        headings.push({ depth, text, id });
      }
    });

    return headings;
  }

  /**
   * Parses 5-state tasks: [ ], [/], [x], [!], [-]
   */
  public static extract5StateTasks(docPath: string, content: string): TaskItem[] {
    const tasks: TaskItem[] = [];
    const lines = content.split('\n');

    lines.forEach((line, index) => {
      const match = line.match(/^\s*-\s*\[([ xX\/!\?-])\]\s*(.+)$/);
      if (match) {
        const marker = match[1];
        const description = match[2].trim();
        let status: TaskStatus = 'open';

        if (marker === 'x' || marker === 'X') {
          status = 'completed';
        } else if (marker === '/') {
          status = 'in-progress';
        } else if (marker === '!' || marker === '?') {
          status = 'blocked';
        } else if (marker === '-') {
          status = 'cancelled';
        } else {
          status = 'open';
        }

        // Check for category tag e.g. [Role: Infra-Arch] or [Category: API]
        const categoryMatch = description.match(/\[(Role|Category|Tag):\s*([^\]]+)\]/i);
        const category = categoryMatch ? categoryMatch[2] : undefined;

        const task: TaskItem = {
          id: `${docPath}-L${index + 1}`,
          documentPath: docPath,
          lineNumber: index + 1,
          description,
          status,
          rawMarker: `[${marker}]`
        };

        if (category) {
          task.category = category;
        }

        tasks.push(task);
      }
    });

    return tasks;
  }
}
